/**
 * IntegrationsTab Component
 * Manage agent integrations (CRMs, Google, Communication, Automation, etc.)
 * Full configuration modal with dynamic fields based on integration type
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Modal, Form, Spinner, Button } from 'react-bootstrap';
import {
  IconPlug,
  IconCheck,
  IconX,
  IconRefresh,
  IconSettings,
  IconBrandGoogle,
  IconChartBar,
  IconCloud,
  IconHeadset,
  IconLifebuoy,
  IconMessages,
  IconMail,
  IconSend,
  IconDeviceMobile,
  IconBolt,
  IconTool,
  IconWebhook,
  IconApi,
  IconUsers,
  IconMessage,
  IconApps,
  IconCode,
  IconAlertCircle,
  IconExternalLink,
  IconUpload,
  IconBook2,
  IconChevronDown,
  IconChevronRight,
  IconBulb,
  IconAlertTriangle,
  IconCalendar,
  IconFolder,
  IconFileText,
  IconFileSpreadsheet,
} from '@tabler/icons-react';
import Swal from 'sweetalert2';
import { useAgentsFlow } from '../../contexts/AgentsFlowContext';
import { useAgentsFlowApi } from '../../hooks/useAgentsFlowApi';
import { INTEGRATIONS, INTEGRATION_CATEGORIES } from '../../constants';
import AgentsFlowService from '../../services/AgentsFlowService';
import RDStationConfig from './RDStationConfig';
import ProBadge from '../ProBadge';
import UpgradeModal from '../UpgradeModal';
import useProFeature, { PRO_INTEGRATION_KEYS } from '../../hooks/useProFeature';
import './IntegrationsTab.css';
import '../UserGuideModal.css';

// ===============================================
// ICON MAPPING
// ===============================================

const INTEGRATION_ICONS = {
  google_workspace: IconBrandGoogle,
  google_calendar: IconCalendar,
  google_sheets: IconFileSpreadsheet,
  google_drive: IconFolder,
  google_docs: IconFileText,
  gmail: IconMail,
  rdstation: IconChartBar,
  hubspot: IconChartBar,
  pipedrive: IconChartBar,
  salesforce: IconCloud,
  zendesk: IconHeadset,
  freshdesk: IconLifebuoy,
  intercom: IconMessages,
  email_smtp: IconMail,
  sendgrid: IconSend,
  twilio: IconDeviceMobile,
  zapier: IconBolt,
  make: IconTool,
  webhook: IconWebhook,
  rest_api: IconApi,
};

const CATEGORY_ICONS = {
  crm: IconUsers,
  support: IconHeadset,
  communication: IconMessage,
  automation: IconBolt,
  productivity: IconApps,
  custom: IconCode,
};

// ===============================================
// INTEGRATION CONFIG FIELD
// ===============================================

const IntegrationConfigField = ({ field, value, onChange }) => {
  const currentValue = value ?? field.default ?? '';

  switch (field.type) {
    case 'boolean':
      return (
        <Form.Check
          type="switch"
          id={`int-${field.key}`}
          label={field.label}
          checked={currentValue}
          onChange={(e) => onChange(field.key, e.target.checked)}
        />
      );

    case 'text':
      return (
        <Form.Group className="mb-3">
          <Form.Label>
            {field.label}
            {field.required && <span className="text-danger ms-1">*</span>}
          </Form.Label>
          <Form.Control
            type="text"
            value={currentValue}
            placeholder={field.placeholder}
            onChange={(e) => onChange(field.key, e.target.value)}
            required={field.required}
          />
        </Form.Group>
      );

    case 'password':
      return (
        <Form.Group className="mb-3">
          <Form.Label>
            {field.label}
            {field.required && <span className="text-danger ms-1">*</span>}
          </Form.Label>
          <Form.Control
            type="password"
            value={currentValue}
            placeholder={field.placeholder || '••••••••'}
            onChange={(e) => onChange(field.key, e.target.value)}
            required={field.required}
          />
        </Form.Group>
      );

    case 'number':
      return (
        <Form.Group className="mb-3">
          <Form.Label>{field.label}</Form.Label>
          <Form.Control
            type="number"
            value={currentValue}
            onChange={(e) => onChange(field.key, parseInt(e.target.value) || 0)}
          />
        </Form.Group>
      );

    case 'select':
      return (
        <Form.Group className="mb-3">
          <Form.Label>{field.label}</Form.Label>
          <Form.Select
            value={currentValue}
            onChange={(e) => onChange(field.key, e.target.value)}
          >
            {field.options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      );

    case 'multiselect':
      return (
        <Form.Group className="mb-3">
          <Form.Label>{field.label}</Form.Label>
          <div className="d-flex flex-wrap gap-2">
            {field.options.map((opt) => (
              <Form.Check
                key={opt}
                type="checkbox"
                id={`int-${field.key}-${opt}`}
                label={field.labels?.[opt] || opt}
                checked={(currentValue || []).includes(opt)}
                onChange={(e) => {
                  const current = currentValue || [];
                  const updated = e.target.checked
                    ? [...current, opt]
                    : current.filter((v) => v !== opt);
                  onChange(field.key, updated);
                }}
              />
            ))}
          </div>
        </Form.Group>
      );

    case 'textarea':
      return (
        <Form.Group className="mb-3">
          <Form.Label>{field.label}</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={currentValue}
            placeholder={field.placeholder}
            onChange={(e) => onChange(field.key, e.target.value)}
          />
        </Form.Group>
      );

    default:
      return null;
  }
};

// ===============================================
// CONNECTED INTEGRATION CARD (Compact Design)
// ===============================================

const ConnectedIntegrationCard = ({ integration, onConfigure, onDisconnect, onTest }) => {
  const info = INTEGRATIONS[integration.integration_type] || {};
  const Icon = INTEGRATION_ICONS[integration.integration_type] || IconPlug;
  const [testing, setTesting] = useState(false);
  const isError = integration.sync_status === 'error';

  const handleTest = async () => {
    setTesting(true);
    await onTest(integration);
    setTesting(false);
  };

  // Format relative time
  const getRelativeTime = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'agora';
    if (diffMins < 60) return `${diffMins}min atrás`;
    if (diffHours < 24) return `${diffHours}h atrás`;
    if (diffDays < 7) return `${diffDays}d atrás`;
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className={`int-card ${isError ? 'int-card--error' : ''}`}>
      <div className="int-card__left">
        <div className={`int-card__icon ${isError ? 'int-card__icon--error' : ''}`}>
          <Icon size={18} />
        </div>
        <div className="int-card__info">
          <div className="int-card__header">
            <span className="int-card__name">{info.name || integration.integration_type}</span>
            <span className={`int-card__status ${isError ? 'int-card__status--error' : ''}`}>
              {isError ? <IconAlertCircle size={10} /> : <IconCheck size={10} />}
              {isError ? 'Erro' : 'Ativo'}
            </span>
          </div>
          {integration.last_sync_at && (
            <span className="int-card__sync">
              Sync: {getRelativeTime(integration.last_sync_at)}
            </span>
          )}
        </div>
      </div>
      <div className="int-card__actions">
        {info.configFields && info.configFields.length > 0 && (
          <button
            className="int-card__btn"
            onClick={() => onConfigure(integration)}
            title="Configurar"
          >
            <IconSettings size={15} />
          </button>
        )}
        <button
          className="int-card__btn"
          onClick={handleTest}
          disabled={testing}
          title="Testar conexão"
        >
          {testing ? <Spinner animation="border" size="sm" /> : <IconRefresh size={15} />}
        </button>
        <button
          className="int-card__btn int-card__btn--danger"
          onClick={() => onDisconnect(integration)}
          title="Desconectar"
        >
          <IconX size={15} />
        </button>
      </div>
    </div>
  );
};

// ===============================================
// AVAILABLE INTEGRATION CARD (Modern Design)
// ===============================================

const CATEGORY_COLORS = {
  crm: { bg: '#fef3c7', color: '#d97706', gradient: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' },
  support: { bg: '#dbeafe', color: '#2563eb', gradient: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' },
  communication: { bg: '#dcfce7', color: '#16a34a', gradient: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)' },
  automation: { bg: '#f3e8ff', color: '#9333ea', gradient: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)' },
  productivity: { bg: '#fee2e2', color: '#dc2626', gradient: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)' },
  custom: { bg: '#f1f5f9', color: '#475569', gradient: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)' },
};

const AvailableIntegrationCard = ({ type, onConnect, isLocked, onProClick }) => {
  const info = INTEGRATIONS[type] || {};
  const Icon = INTEGRATION_ICONS[type] || IconPlug;
  const categoryColor = CATEGORY_COLORS[info.category] || CATEGORY_COLORS.custom;

  return (
    <div
      className={`avail-card ${isLocked ? 'avail-card--locked' : ''}`}
      onClick={() => isLocked ? onProClick?.(type) : onConnect(type)}
    >
      <div className="avail-card__icon" style={{ background: categoryColor.gradient, opacity: isLocked ? 0.5 : 1 }}>
        <Icon size={18} style={{ color: categoryColor.color }} />
      </div>
      <div className="avail-card__content">
        <span className="avail-card__name" style={{ opacity: isLocked ? 0.6 : 1 }}>
          {info.name} {isLocked && <ProBadge small inline />}
        </span>
        <span className="avail-card__desc" style={{ opacity: isLocked ? 0.5 : 1 }}>{info.description}</span>
      </div>
      <div className="avail-card__action" style={{ opacity: isLocked ? 0.3 : 1 }}>
        <IconPlug size={16} />
      </div>
    </div>
  );
};

// ===============================================
// SERVICE ACCOUNT FORM (Gmail Workspace)
// ===============================================

const GMAIL_GUIDE_SECTIONS = [
  {
    id: 'requirements',
    title: '1. Requisitos',
    content: (
      <>
        <p>Para conectar o Gmail ao agente, voce precisa de:</p>
        <ul>
          <li>Uma conta <strong>Google Workspace</strong> (email corporativo, ex: voce@suaempresa.com)</li>
          <li>Acesso de <strong>Super Administrador</strong> ao Admin Console do Workspace</li>
          <li>Um projeto no <strong>Google Cloud Console</strong></li>
        </ul>
        <div className="guide-callout guide-callout--warning">
          <IconAlertTriangle size={16} />
          <div>
            <strong>Contas pessoais @gmail.com nao sao compativeis.</strong> Esta integracao funciona
            exclusivamente com Google Workspace.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'create-project',
    title: '2. Criar projeto no Google Cloud',
    content: (
      <>
        <ol style={{ lineHeight: 2 }}>
          <li>Acesse <strong>console.cloud.google.com</strong></li>
          <li>Clique em <strong>Select a project</strong> (topo) e depois em <strong>New Project</strong></li>
          <li>De um nome ao projeto (ex: "Mercado Agentes") e clique em <strong>Create</strong></li>
          <li>Selecione o projeto criado</li>
        </ol>
        <div className="guide-callout guide-callout--info">
          <IconBulb size={16} />
          <div>Se voce ja tem um projeto no Google Cloud, pode reutiliza-lo. Pule para o proximo passo.</div>
        </div>
      </>
    ),
  },
  {
    id: 'enable-api',
    title: '3. Habilitar Gmail API',
    content: (
      <>
        <ol style={{ lineHeight: 2 }}>
          <li>No Google Cloud Console, va em <strong>APIs & Services → Library</strong></li>
          <li>Pesquise por <strong>"Gmail API"</strong></li>
          <li>Clique em <strong>Gmail API</strong> e depois em <strong>Enable</strong></li>
        </ol>
      </>
    ),
  },
  {
    id: 'create-sa',
    title: '4. Criar Service Account',
    content: (
      <>
        <ol style={{ lineHeight: 2 }}>
          <li>Va em <strong>IAM & Admin → Service Accounts</strong></li>
          <li>Clique em <strong>Create Service Account</strong></li>
          <li>Nome: ex. <code>mercado-agentes-gmail</code></li>
          <li>Clique em <strong>Create and Continue</strong> e depois em <strong>Done</strong></li>
          <li>Na lista, clique na Service Account criada</li>
          <li>Va na aba <strong>Keys</strong></li>
          <li>Clique em <strong>Add Key → Create new key → JSON</strong></li>
          <li>Salve o arquivo <code>.json</code> que sera baixado (voce vai usa-lo aqui na plataforma)</li>
        </ol>
        <div className="guide-callout guide-callout--warning">
          <IconAlertTriangle size={16} />
          <div>
            <strong>Guarde o arquivo JSON em local seguro.</strong> Ele contem a chave privada
            da Service Account e nao pode ser baixado novamente.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'get-client-id',
    title: '5. Copiar o Client ID',
    content: (
      <>
        <ol style={{ lineHeight: 2 }}>
          <li>Na pagina da Service Account, va em <strong>Details</strong></li>
          <li>Expanda <strong>Advanced settings</strong></li>
          <li>Copie o <strong>Client ID</strong> (um numero longo, ex: <code>117820349823456789012</code>)</li>
          <li>Voce vai usar este ID no proximo passo</li>
        </ol>
      </>
    ),
  },
  {
    id: 'delegation',
    title: '6. Configurar Domain-Wide Delegation',
    content: (
      <>
        <p>Este e o passo mais importante. Voce precisa ser <strong>Super Admin</strong> do Workspace.</p>
        <ol style={{ lineHeight: 2 }}>
          <li>Acesse <strong>admin.google.com</strong></li>
          <li>Va em <strong>Seguranca → Controles de API</strong></li>
          <li>Na secao "Delegacao em todo o dominio", clique em <strong>Gerenciar a delegacao em todo o dominio</strong></li>
          <li>Ou acesse diretamente: <code>admin.google.com/ac/owl/domainwidedelegation</code></li>
          <li>Clique em <strong>Adicionar novo</strong></li>
          <li>Cole o <strong>Client ID</strong> copiado no passo anterior</li>
          <li>No campo <strong>Escopos OAuth</strong>, cole exatamente:
            <div className="guide-code" style={{ marginTop: '8px', fontSize: '11px', wordBreak: 'break-all' }}>
              https://www.googleapis.com/auth/gmail.readonly,https://www.googleapis.com/auth/gmail.send,https://www.googleapis.com/auth/gmail.modify
            </div>
          </li>
          <li>Clique em <strong>Autorizar</strong></li>
        </ol>
        <div className="guide-callout guide-callout--info">
          <IconBulb size={16} />
          <div>
            A propagacao pode levar alguns minutos. Se o teste de conexao falhar logo apos
            configurar, aguarde 2-5 minutos e tente novamente.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'connect',
    title: '7. Conectar na plataforma',
    content: (
      <>
        <ol style={{ lineHeight: 2 }}>
          <li>Feche este guia</li>
          <li>Faca <strong>upload do arquivo JSON</strong> da Service Account (ou cole o conteudo)</li>
          <li>Informe o <strong>email do usuario Workspace</strong> que o agente ira usar (ex: vendas@suaempresa.com)</li>
          <li>Clique em <strong>Testar conexao</strong> para validar</li>
          <li>Se o teste passar, clique em <strong>Conectar</strong></li>
        </ol>
      </>
    ),
  },
  {
    id: 'errors',
    title: '8. Erros comuns',
    content: (
      <>
        <table className="guide-table">
          <thead><tr><th>Erro</th><th>Causa</th><th>Solucao</th></tr></thead>
          <tbody>
            <tr>
              <td><code>unauthorized_client</code></td>
              <td>Delegation nao configurada</td>
              <td>Refaca o passo 6 no Admin Console</td>
            </tr>
            <tr>
              <td><code>invalid_grant</code></td>
              <td>Email nao pertence ao dominio</td>
              <td>Use um email @seudominio.com, nao @gmail.com</td>
            </tr>
            <tr>
              <td><code>access_denied</code></td>
              <td>Escopos nao autorizados</td>
              <td>Verifique se colou os 3 escopos corretamente</td>
            </tr>
            <tr>
              <td>Erro 403 no Admin Console</td>
              <td>Nao e Super Admin</td>
              <td>Peca ao admin do dominio para configurar</td>
            </tr>
          </tbody>
        </table>
      </>
    ),
  },
];

const GmailGuideModal = ({ show, onHide }) => {
  const [openSections, setOpenSections] = useState(['requirements']);

  const toggleSection = (id) => {
    setOpenSections((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const expandAll = () => setOpenSections(GMAIL_GUIDE_SECTIONS.map((s) => s.id));
  const collapseAll = () => setOpenSections([]);

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="user-guide-modal" scrollable>
      <Modal.Header closeButton className="user-guide-modal__header">
        <Modal.Title className="user-guide-modal__title">
          <div className="user-guide-modal__title-icon">
            <IconBook2 size={22} />
          </div>
          Guia de Configuracao — Gmail (Workspace)
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="user-guide-modal__body">
        <p className="user-guide-modal__intro">
          Siga os passos abaixo para configurar a integracao do Gmail via Service Account.
          Clique em cada secao para expandir.
        </p>
        <div className="user-guide-modal__controls">
          <button className="user-guide-modal__control-btn" onClick={expandAll}>Expandir tudo</button>
          <button className="user-guide-modal__control-btn" onClick={collapseAll}>Recolher tudo</button>
        </div>
        <div className="guide-sections">
          {GMAIL_GUIDE_SECTIONS.map((section) => (
            <div key={section.id} className={`guide-section ${openSections.includes(section.id) ? 'guide-section--open' : ''}`}>
              <button className="guide-section__header" onClick={() => toggleSection(section.id)}>
                <span className="guide-section__title">{section.title}</span>
                {openSections.includes(section.id) ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
              </button>
              {openSections.includes(section.id) && (
                <div className="guide-section__content">{section.content}</div>
              )}
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer className="user-guide-modal__footer">
        <Button variant="primary" size="sm" onClick={onHide}>Fechar</Button>
      </Modal.Footer>
    </Modal>
  );
};

const ServiceAccountForm = ({ integrationType, agentId, onSuccess }) => {
  const [saJson, setSaJson] = useState('');
  const [delegatedEmail, setDelegatedEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [showGuide, setShowGuide] = useState(false);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadedFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        JSON.parse(ev.target.result);
        setSaJson(ev.target.result);
        setTestResult(null);
      } catch {
        Swal.fire({ icon: 'error', title: 'JSON inválido', text: 'O arquivo não contém um JSON válido.' });
      }
    };
    reader.readAsText(file);
  };

  const handleTest = async () => {
    if (!saJson || !delegatedEmail) {
      Swal.fire({ icon: 'warning', title: 'Campos obrigatórios', text: 'Preencha o JSON da Service Account e o email.' });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const { api } = await import('../../../../api/Middleware');
      const { API_URL } = await import('../../../../api/Urls');
      const response = await api.post(`${API_URL}agents_flow/google/test_gmail_service_account/`, {
        service_account_json: saJson,
        delegated_email: delegatedEmail,
      });
      setTestResult(response.data);
    } catch (err) {
      setTestResult({ success: false, error: err.response?.data?.error || 'Erro ao testar conexão' });
    } finally {
      setTesting(false);
    }
  };

  const handleConnect = async () => {
    if (!saJson || !delegatedEmail) {
      Swal.fire({ icon: 'warning', title: 'Campos obrigatórios', text: 'Preencha o JSON da Service Account e o email.' });
      return;
    }
    setLoading(true);
    try {
      const { api } = await import('../../../../api/Middleware');
      const { API_URL } = await import('../../../../api/Urls');
      const response = await api.post(`${API_URL}agents_flow/google/connect_gmail_service_account/`, {
        agent_flow_id: agentId,
        service_account_json: saJson,
        delegated_email: delegatedEmail,
      });
      if (response.data?.success) {
        onSuccess();
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao conectar',
        text: err.response?.data?.error || 'Não foi possível conectar a Service Account.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <GmailGuideModal show={showGuide} onHide={() => setShowGuide(false)} />

      {/* Botão Guia */}
      <button
        onClick={() => setShowGuide(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px', alignSelf: 'flex-start',
          padding: '8px 16px', border: '1px solid #3b82f6', borderRadius: '8px',
          background: '#eff6ff', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
          color: '#2563eb', transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#dbeafe'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = '#eff6ff'; }}
      >
        <IconBook2 size={16} />
        Guia de configuracao
      </button>

      <div style={{ padding: '14px 16px', background: '#fefce8', borderRadius: '8px', border: '1px solid #fde68a', marginBottom: '4px' }}>
        <p style={{ fontSize: '14px', fontWeight: 600, color: '#92400e', margin: '0 0 6px 0' }}>
          Disponivel apenas para Google Workspace
        </p>
        <p style={{ fontSize: '13px', color: '#78350f', margin: '0 0 8px 0', lineHeight: 1.6 }}>
          Esta integração funciona apenas com contas <strong>Google Workspace</strong> (email corporativo).
          Contas pessoais do Gmail (<strong>@gmail.com</strong>) <strong>não são compatíveis</strong>.
        </p>
        <p style={{ fontSize: '13px', color: '#78350f', margin: 0, lineHeight: 1.6 }}>
          O administrador do domínio Workspace precisa:
        </p>
        <ol style={{ fontSize: '12px', color: '#92400e', margin: '6px 0 0 0', paddingLeft: '18px', lineHeight: 1.8 }}>
          <li>Criar uma <strong>Service Account</strong> no Google Cloud Console</li>
          <li>Habilitar a <strong>Gmail API</strong> no projeto</li>
          <li>Configurar <strong>Domain-Wide Delegation</strong> no Admin Console do Workspace</li>
          <li>Gerar a <strong>chave JSON</strong> e fornecer abaixo junto com o email</li>
        </ol>
      </div>

      {testResult && (
        <div style={{
          padding: '10px 14px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px',
          background: testResult.success ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${testResult.success ? '#bbf7d0' : '#fecaca'}`,
        }}>
          {testResult.success ? <IconCheck size={16} style={{ color: '#16a34a' }} /> : <IconAlertCircle size={16} style={{ color: '#dc2626' }} />}
          <span style={{ fontSize: '13px', color: testResult.success ? '#15803d' : '#dc2626' }}>
            {testResult.success ? `Conectado: ${testResult.email}` : testResult.error}
          </span>
        </div>
      )}

      {/* JSON da Service Account */}
      <div>
        <label style={{ fontWeight: 500, fontSize: '13px', color: '#374151', display: 'block', marginBottom: '6px' }}>
          JSON da Service Account *
        </label>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <label style={{
            padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
            fontSize: '13px', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '6px',
            background: '#f9fafb',
          }}>
            <IconUpload size={16} />
            {uploadedFileName || 'Upload arquivo .json'}
            <input type="file" accept=".json" onChange={handleFileUpload} style={{ display: 'none' }} />
          </label>
        </div>
        <textarea
          value={saJson}
          onChange={(e) => { setSaJson(e.target.value); setTestResult(null); }}
          placeholder='{"type": "service_account", "project_id": "...", ...}'
          rows={6}
          style={{
            width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px',
            fontSize: '12px', fontFamily: 'monospace', resize: 'vertical', outline: 'none',
          }}
        />
        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
          Cole o conteúdo do arquivo JSON da Service Account ou faça upload
        </div>
      </div>

      {/* Email delegado */}
      <div>
        <label style={{ fontWeight: 500, fontSize: '13px', color: '#374151', display: 'block', marginBottom: '6px' }}>
          Email do usuário Workspace *
        </label>
        <input
          type="email"
          value={delegatedEmail}
          onChange={(e) => { setDelegatedEmail(e.target.value); setTestResult(null); }}
          placeholder="usuario@empresa.com"
          style={{
            width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px',
            fontSize: '14px', outline: 'none',
          }}
        />
        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
          Email da conta Google Workspace que o agente irá usar para ler e enviar emails
        </div>
      </div>

      {/* Buttons */}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
        <button
          onClick={handleTest}
          disabled={testing || !saJson || !delegatedEmail}
          style={{
            padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer',
            fontSize: '14px', fontWeight: 500, color: '#374151', background: '#fff',
            display: 'flex', alignItems: 'center', gap: '6px',
            opacity: (testing || !saJson || !delegatedEmail) ? 0.5 : 1,
          }}
        >
          {testing ? <Spinner animation="border" size="sm" /> : <IconRefresh size={16} />}
          Testar conexão
        </button>
        <button
          onClick={handleConnect}
          disabled={loading || !saJson || !delegatedEmail}
          style={{
            padding: '10px 20px', border: 'none', borderRadius: '8px', cursor: 'pointer',
            fontSize: '14px', fontWeight: 500, color: '#fff', background: '#3b82f6',
            display: 'flex', alignItems: 'center', gap: '6px',
            opacity: (loading || !saJson || !delegatedEmail) ? 0.5 : 1,
          }}
        >
          {loading ? <Spinner animation="border" size="sm" /> : <IconCheck size={16} />}
          Conectar
        </button>
      </div>
    </div>
  );
};

// ===============================================
// CONNECTION MODAL (NEW/EDIT)
// ===============================================

const ConnectionModal = ({ show, onHide, integrationType, integration, onSave, agentId }) => {
  const info = INTEGRATIONS[integrationType] || {};
  const Icon = INTEGRATION_ICONS[integrationType] || IconPlug;
  const isEditing = !!integration;

  const [config, setConfig] = useState(() => {
    // Default values from config fields
    const defaults = {};
    (info.configFields || []).forEach((field) => {
      if (field.default !== undefined) {
        defaults[field.key] = field.default;
      }
    });
    if (integration?.configuration) {
      return { ...defaults, ...integration.configuration, ...integration.credentials };
    }
    return defaults;
  });
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleFieldChange = useCallback((key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setTestResult(null);
  }, []);

  const handleConnect = async () => {
    // Validate required fields
    const requiredFields = (info.configFields || []).filter((f) => f.required);
    const missingFields = requiredFields.filter((f) => !config[f.key]);

    if (missingFields.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Campos obrigatórios',
        text: `Preencha: ${missingFields.map((f) => f.label).join(', ')}`,
      });
      return;
    }

    setLoading(true);
    try {
      // Separate credentials from configuration
      const credentialKeys = ['api_key', 'api_token', 'auth_token', 'access_token', 'password', 'auth_value', 'account_sid'];
      const credentials = {};
      const configuration = {};

      Object.entries(config).forEach(([key, value]) => {
        if (credentialKeys.includes(key)) {
          // Só incluir credenciais que foram preenchidas (não sobrescrever salvas com vazio)
          if (value) {
            credentials[key] = value;
          }
        } else {
          configuration[key] = value;
        }
      });

      // Build payload - don't send empty credentials (protects OAuth tokens)
      const payload = {
        integration_type: integrationType,
        configuration,
        is_active: true,
      };

      // Only include credentials if not empty (OAuth integrations manage their own tokens)
      if (Object.keys(credentials).length > 0) {
        payload.credentials = credentials;
      }

      await onSave(payload);

      onHide();
      Swal.fire({
        icon: 'success',
        title: isEditing ? 'Configuração atualizada!' : 'Integração conectada!',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: error.message || 'Não foi possível salvar a configuração.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthConnect = async () => {
    // Check if it's a Google integration (includes 'gmail' which doesn't start with 'google')
    if (integrationType.startsWith('google') || integrationType === 'gmail') {
      try {
        // First, call the authorize API to get the auth URL
        const { api } = await import('../../../../api/Middleware');
        const { API_URL } = await import('../../../../api/Urls');

        const response = await api.get(`${API_URL}agents_flow/google/authorize/`, {
          params: {
            agent_flow_id: agentId,
            integration_type: integrationType
          }
        });

        const authUrl = response.data?.auth_url;
        if (!authUrl) {
          Swal.fire({
            icon: 'error',
            title: 'Erro',
            text: 'Não foi possível obter a URL de autorização.',
          });
          return;
        }

        // Open the Google OAuth URL in popup
        const popup = window.open(authUrl, 'google-oauth', 'width=600,height=700');

        // Listen for OAuth callback
        const handleMessage = (event) => {
          console.log('Received postMessage:', event.data, 'from origin:', event.origin);

          // Check if it's a Google OAuth message
          if (event.data?.type === 'google-oauth-success') {
            console.log('Google OAuth success received!');
            popup?.close();
            window.removeEventListener('message', handleMessage);
            clearInterval(checkClosed);
            onHide();
            Swal.fire({
              icon: 'success',
              title: 'Google conectado!',
              text: event.data?.email
                ? `Conta ${event.data.email} conectada com sucesso.`
                : 'A integração com Google foi configurada com sucesso.',
              timer: 2500,
              showConfirmButton: false,
            }).then(() => {
              // Reload to refresh integrations
              window.location.reload();
            });
          } else if (event.data?.type === 'google-oauth-error') {
            console.log('Google OAuth error received:', event.data?.error);
            popup?.close();
            window.removeEventListener('message', handleMessage);
            clearInterval(checkClosed);
            Swal.fire({
              icon: 'error',
              title: 'Erro na autorização',
              text: event.data?.error || 'Não foi possível conectar com o Google.',
            });
          }
        };

        window.addEventListener('message', handleMessage);

        // Cleanup listener if popup is closed manually
        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            console.log('Popup closed manually, cleaning up listener');
            clearInterval(checkClosed);
            window.removeEventListener('message', handleMessage);
            // Reload anyway in case auth succeeded but message wasn't received
            Swal.fire({
              icon: 'info',
              title: 'Verificando...',
              text: 'Verificando status da integração...',
              timer: 1500,
              showConfirmButton: false,
            }).then(() => {
              window.location.reload();
            });
          }
        }, 1000);

      } catch (error) {
        console.error('Error initiating Google OAuth:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: error.response?.data?.error || 'Não foi possível iniciar a autorização com o Google.',
        });
      }

      return;
    }

    // For other OAuth integrations, use standard flow
    const oauthUrl = `/api/v1/agents_flow/agents/${agentId}/integrations/oauth/${integrationType}/`;
    window.open(oauthUrl, '_blank', 'width=600,height=700');
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="int-modal">
      <div className="int-modal__header">
        <button className="int-modal__close" onClick={onHide}>
          <IconX size={18} />
        </button>
        <div className="int-modal__icon-wrapper">
          <div className="int-modal__icon">
            <Icon size={28} />
          </div>
        </div>
        <h2 className="int-modal__title">
          {isEditing ? `Configurar ${info.name}` : `Conectar ${info.name}`}
        </h2>
        {info.description && (
          <p className="int-modal__subtitle">{info.description}</p>
        )}
      </div>

      <div className="int-modal__body">
        {info.authType === 'oauth' && !isEditing ? (
          <div className="int-modal__oauth">
            <div className="int-modal__oauth-visual">
              <div className="int-modal__oauth-dot int-modal__oauth-dot--app">
                <IconPlug size={20} />
              </div>
              <div className="int-modal__oauth-line">
                <div className="int-modal__oauth-line-pulse"></div>
              </div>
              <div className="int-modal__oauth-dot int-modal__oauth-dot--service">
                <Icon size={20} />
              </div>
            </div>
            <div className="int-modal__oauth-info">
              <p className="int-modal__oauth-text">
                Conecte sua conta do <strong>{info.name}</strong> para sincronizar dados automaticamente.
              </p>
              <ul className="int-modal__oauth-features">
                <li><IconCheck size={14} /> Conexão segura via OAuth 2.0</li>
                <li><IconCheck size={14} /> Sincronização automática</li>
                <li><IconCheck size={14} /> Você pode desconectar a qualquer momento</li>
              </ul>
            </div>
            <button className="int-modal__oauth-btn" onClick={handleOAuthConnect}>
              <Icon size={20} />
              Conectar com {info.name}
              <IconExternalLink size={16} />
            </button>
          </div>
        ) : info.authType === 'service_account' && !isEditing ? (
          <ServiceAccountForm
            integrationType={integrationType}
            agentId={agentId}
            onSuccess={() => {
              onHide();
              Swal.fire({
                icon: 'success',
                title: 'Gmail conectado!',
                text: 'Service Account configurada com sucesso.',
                timer: 2500,
                showConfirmButton: false,
              }).then(() => window.location.reload());
            }}
          />
        ) : (
          <>
            {testResult && (
              <div className={`int-modal__alert ${testResult.success ? 'int-modal__alert--success' : 'int-modal__alert--error'}`}>
                {testResult.success ? <IconCheck size={16} /> : <IconAlertCircle size={16} />}
                <span>{testResult.success ? 'Conexão testada com sucesso!' : (testResult.error || 'Falha ao testar conexão')}</span>
              </div>
            )}

            <div className="int-modal__form">
              {(info.configFields || []).map((field) => {
                // Para campos password em edição, mostrar placeholder se credencial já está salva
                const fieldWithHint = (isEditing && field.type === 'password' && integration?.has_credentials)
                  ? { ...field, placeholder: 'Salvo - preencha apenas para alterar' }
                  : field;
                return (
                  <div key={field.key} className={`int-modal__field ${field.type === 'textarea' ? 'int-modal__field--full' : ''}`}>
                    <IntegrationConfigField
                      field={fieldWithHint}
                      value={config[field.key]}
                      onChange={handleFieldChange}
                    />
                  </div>
                );
              })}

              {(!info.configFields || info.configFields.length === 0) && !isEditing && (
                <div className="int-modal__empty">
                  <IconCheck size={24} />
                  <span>Esta integração não requer configuração adicional</span>
                </div>
              )}
            </div>

            {isEditing && integrationType === 'google_calendar' && integration?.id && (
              <div className="int-modal__section">
                <div className="int-modal__section-header">
                  <IconBrandGoogle size={18} />
                  <span>Google Calendar</span>
                </div>
                <div style={{ padding: '12px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <IconCheck size={16} style={{ color: '#16a34a' }} />
                    <strong style={{ fontSize: '14px', color: '#15803d' }}>Conexão OAuth ativa</strong>
                  </div>
                  <p style={{ fontSize: '13px', color: '#166534', margin: 0 }}>
                    A configuração de calendários, profissionais e serviços agora é feita diretamente
                    no <strong>nó de Agendamento</strong> dentro do fluxo visual do agente.
                  </p>
                </div>
              </div>
            )}

            {isEditing && integrationType === 'rdstation' && integration?.id && (
              <div className="int-modal__section" style={{minHeight: 200}}>
                <div className="int-modal__section-header">
                  <IconChartBar size={18} />
                  <span>Configuração do RD Station CRM</span>
                </div>
                <RDStationConfig
                  agentId={agentId}
                  integrationId={integration.id}
                  config={config}
                  onConfigChange={(newConfig) => {
                    setConfig((prev) => ({ ...prev, ...newConfig }));
                  }}
                />
              </div>
            )}

          </>
        )}
      </div>

      {(info.authType !== 'oauth' && info.authType !== 'service_account' || isEditing) && (
        <div className="int-modal__footer">
          <button className="int-modal__btn int-modal__btn--secondary" onClick={onHide}>
            Cancelar
          </button>
          <button
            className="int-modal__btn int-modal__btn--primary"
            onClick={handleConnect}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" />
                {isEditing ? 'Salvando...' : 'Conectando...'}
              </>
            ) : (
              <>
                <IconCheck size={18} />
                {isEditing ? 'Salvar' : 'Conectar'}
              </>
            )}
          </button>
        </div>
      )}
    </Modal>
  );
};

// ===============================================
// MAIN COMPONENT
// ===============================================

const IntegrationsTab = ({ agentId }) => {
  const { state, updateIntegrations } = useAgentsFlow();
  const { connectIntegration, disconnectIntegration, updateIntegration, testIntegration } = useAgentsFlowApi();
  const { editor } = state;
  const { isProPlan, upgradeModal, openUpgradeModal, closeUpgradeModal } = useProFeature();
  const { integrations } = editor;

  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  // Get connected integration types
  const connectedTypes = integrations.map((i) => i.integration_type);

  // Group available integrations by category
  const integrationsByCategory = useMemo(() => {
    const grouped = { all: [] };
    INTEGRATION_CATEGORIES.forEach((cat) => {
      grouped[cat.id] = [];
    });

    Object.entries(INTEGRATIONS).forEach(([key, info]) => {
      if (!connectedTypes.includes(key)) {
        grouped.all.push(key);
        if (info.category && grouped[info.category]) {
          grouped[info.category].push(key);
        }
      }
    });

    return grouped;
  }, [connectedTypes]);

  const handleConnect = useCallback((type) => {
    setSelectedType(type);
    setSelectedIntegration(null);
    setShowModal(true);
  }, []);

  const handleConfigure = useCallback((integration) => {
    setSelectedType(integration.integration_type);
    setSelectedIntegration(integration);
    setShowModal(true);
  }, []);

  const handleSaveConnection = useCallback(async (data) => {
    if (selectedIntegration) {
      // Update existing
      const updated = integrations.map((i) =>
        i.id === selectedIntegration.id
          ? { ...i, ...data, configuration: { ...i.configuration, ...data.configuration } }
          : i
      );
      updateIntegrations(updated);

      if (agentId && agentId !== 'novo') {
        await updateIntegration(agentId, selectedIntegration.id, data);
      }
    } else {
      // Create new
      const newIntegration = {
        id: Date.now(),
        ...data,
        sync_status: 'active',
        created_at: new Date().toISOString(),
      };
      updateIntegrations([...integrations, newIntegration]);

      if (agentId && agentId !== 'novo') {
        await connectIntegration(agentId, data);
      }
    }
  }, [integrations, updateIntegrations, connectIntegration, updateIntegration, agentId, selectedIntegration]);

  const handleDisconnect = useCallback(async (integration) => {
    const result = await Swal.fire({
      title: 'Desconectar integração?',
      text: `Deseja desconectar ${INTEGRATIONS[integration.integration_type]?.name}? Isso pode afetar funcionalidades do agente.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Sim, desconectar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      const updated = integrations.filter((i) => i.id !== integration.id);
      updateIntegrations(updated);

      if (agentId && agentId !== 'novo') {
        try {
          await disconnectIntegration(agentId, integration.id);
        } catch (error) {
          updateIntegrations(integrations);
          throw error;
        }
      }

      Swal.fire({
        icon: 'success',
        title: 'Desconectado!',
        timer: 1500,
        showConfirmButton: false,
      });
    }
  }, [integrations, updateIntegrations, disconnectIntegration, agentId]);

  // Verifica se há integrações Google conectadas
  const googleTypes = ['gmail', 'google_calendar', 'google_sheets', 'google_drive', 'google_docs', 'google_workspace'];
  const hasGoogleIntegrations = integrations.some((i) => googleTypes.includes(i.integration_type));

  const handleRevokeGoogle = useCallback(async () => {
    const result = await Swal.fire({
      title: 'Desconectar conta Google?',
      html: `
        <div style="text-align:left;font-size:14px;line-height:1.6">
          <p>Esta ação irá:</p>
          <ul style="margin:8px 0">
            <li>Revogar <strong>todas as permissões</strong> do Google (Gmail, Calendar, Sheets, Drive)</li>
            <li>Remover <strong>todas as integrações Google</strong> deste agente</li>
            <li>Remover o app <strong>Mercado Agentes</strong> da sua conta Google</li>
          </ul>
          <p style="color:#ef4444;font-weight:600;margin-top:12px">
            ⚠️ Se outros agentes da sua conta usam a mesma conta Google, eles também perderão o acesso e precisarão ser reconectados.
          </p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Sim, desconectar tudo',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        const response = await AgentsFlowService.revokeGoogleAccess(agentId);
        const data = response.data;

        // Remover integrações Google do state local
        const updated = integrations.filter((i) => !googleTypes.includes(i.integration_type));
        updateIntegrations(updated);

        Swal.fire({
          icon: 'success',
          title: 'Google desconectado!',
          text: data.message || `${data.integrations_removed} integração(ões) removida(s).`,
          timer: 3000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error('Error revoking Google access:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erro ao desconectar',
          text: error.response?.data?.error || 'Não foi possível revogar o acesso Google.',
        });
      }
    }
  }, [agentId, integrations, updateIntegrations]);

  const handleTest = useCallback(async (integration) => {
    if (agentId && agentId !== 'novo') {
      try {
        const result = await testIntegration(agentId, integration.id);

        if (result?.success) {
          Swal.fire({
            icon: 'success',
            title: 'Conexão OK!',
            text: 'A integração está funcionando corretamente.',
            timer: 2000,
            showConfirmButton: false,
          });

          // Update status
          const updated = integrations.map((i) =>
            i.id === integration.id ? { ...i, sync_status: 'active', last_sync_at: new Date().toISOString() } : i
          );
          updateIntegrations(updated);
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Falha na conexão',
            text: result?.error || 'Verifique as credenciais e tente novamente.',
          });

          // Update status
          const updated = integrations.map((i) =>
            i.id === integration.id ? { ...i, sync_status: 'error' } : i
          );
          updateIntegrations(updated);
        }
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Erro ao testar',
          text: error.message || 'Não foi possível testar a conexão.',
        });
      }
    } else {
      // Simulate for new agents
      Swal.fire({
        icon: 'info',
        title: 'Teste simulado',
        text: 'Salve o agente primeiro para testar a conexão real.',
        timer: 2000,
        showConfirmButton: false,
      });
    }
  }, [agentId, integrations, updateIntegrations, testIntegration]);

  return (
    <div className="tab-content-wrapper">
      {/* Connected Integrations */}
      <section className="editor-section">
        <h3 className="editor-section-title">Integrações Conectadas</h3>
        <p className="text-muted mb-4">
          Gerencie as integrações ativas do seu agente.
        </p>

        {integrations.length === 0 ? (
          <div className="empty-integrations text-center py-4">
            <IconPlug size={48} className="text-muted mb-3" />
            <p className="text-muted">Nenhuma integração conectada ainda.</p>
            <p className="text-muted small">
              Conecte CRMs, ferramentas de automação e outros serviços para expandir as capacidades do seu agente.
            </p>
          </div>
        ) : (
          <>
            <div className="integrations-grid">
              {integrations.map((integration) => (
                <ConnectedIntegrationCard
                  key={integration.id}
                  integration={integration}
                  onConfigure={handleConfigure}
                  onDisconnect={handleDisconnect}
                  onTest={handleTest}
                />
              ))}
            </div>

            {hasGoogleIntegrations && (
              <button
                className="btn btn-outline-danger btn-sm mt-3 d-flex align-items-center gap-1"
                onClick={handleRevokeGoogle}
              >
                <IconBrandGoogle size={14} />
                Desconectar conta Google
              </button>
            )}
          </>
        )}
      </section>

      {/* Available Integrations */}
      <section className="editor-section">
        <div className="avail-header">
          <div>
            <h3 className="avail-header__title">Adicionar Integração</h3>
            <p className="avail-header__subtitle">
              Conecte CRMs, automações e outros serviços
            </p>
          </div>
          <div className="avail-header__count">
            {integrationsByCategory.all?.length || 0} disponíveis
          </div>
        </div>

        <div className="avail-filters">
          <button
            className={`avail-filter ${activeCategory === 'all' ? 'avail-filter--active' : ''}`}
            onClick={() => setActiveCategory('all')}
          >
            Todos
          </button>
          {INTEGRATION_CATEGORIES.map((cat) => {
            const CategoryIcon = CATEGORY_ICONS[cat.id] || IconPlug;
            const count = integrationsByCategory[cat.id]?.length || 0;
            if (count === 0) return null;
            const catColor = CATEGORY_COLORS[cat.id] || CATEGORY_COLORS.custom;

            return (
              <button
                key={cat.id}
                className={`avail-filter ${activeCategory === cat.id ? 'avail-filter--active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
                style={activeCategory === cat.id ? { '--filter-color': catColor.color } : {}}
              >
                <CategoryIcon size={14} />
                {cat.name}
              </button>
            );
          })}
        </div>

        {(integrationsByCategory[activeCategory] || []).length === 0 ? (
          <div className="avail-empty">
            <IconCheck size={24} />
            <span>Todas as integrações desta categoria já estão conectadas</span>
          </div>
        ) : (
          <div className="avail-grid">
            {(integrationsByCategory[activeCategory] || []).map((type) => (
              <AvailableIntegrationCard
                key={type}
                type={type}
                onConnect={handleConnect}
                isLocked={PRO_INTEGRATION_KEYS.includes(type) && !isProPlan}
                onProClick={(t) => {
                  const info = INTEGRATIONS[t] || {};
                  const Icon = INTEGRATION_ICONS[t] || IconPlug;
                  openUpgradeModal({
                    featureName: info.name || t,
                    featureDescription: info.description || 'Integração premium disponível nos planos Founder e Business.',
                    featureIcon: Icon,
                  });
                }}
              />
            ))}
          </div>
        )}
      </section>

      {/* Connection/Configuration Modal */}
      <ConnectionModal
        key={selectedIntegration?.id || selectedType || 'new'}
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setSelectedType(null);
          setSelectedIntegration(null);
        }}
        integrationType={selectedType}
        integration={selectedIntegration}
        onSave={handleSaveConnection}
        agentId={agentId}
      />
      <UpgradeModal
        show={upgradeModal.show}
        onClose={closeUpgradeModal}
        featureName={upgradeModal.featureName}
        featureDescription={upgradeModal.featureDescription}
        featureIcon={upgradeModal.featureIcon}
        featureGradient={upgradeModal.featureGradient}
      />
    </div>
  );
};

export default IntegrationsTab;
